/**
 * Site-wide Mojo Agent shell — overlay drawer (mobile / overlay mode) or pushed sidebar (desktop push mode).
 */
import React, { useEffect, useRef } from 'react';
import { useMojoAgent } from '../../contexts/MojoAgentContext';
import { MojoAgentPanel } from './MojoAgentPanel';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function MojoAgentShell(): React.ReactElement | null {
  const { isOpen, close, layoutMode, panelWidth, isWideOverlay } = useMojoAgent();
  const panelRef = useRef<HTMLDivElement>(null);
  const isPushLayout = layoutMode === 'push';
  const isWide = panelWidth === 'wide' && !isPushLayout;

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }

      if (e.key !== 'Tab' || !panelRef.current) return;

      const isMobile = window.matchMedia('(max-width: 1023px)').matches;
      if (!isMobile) return;

      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  if (!isOpen) {
    return null;
  }

  const overlayWidthClasses = isWide
    ? 'fixed inset-y-0 right-0 z-[80] w-full max-w-md shadow-xl lg:w-[90%] lg:max-w-[90%]'
    : 'fixed inset-y-0 right-0 z-[80] w-full max-w-md shadow-xl lg:w-[400px] lg:max-w-[400px]';

  return (
    <>
      <div
        className={[
          'fixed inset-0 z-[70] bg-black/40',
          isWideOverlay ? 'block' : 'lg:hidden',
        ].join(' ')}
        aria-hidden="true"
        onClick={close}
        data-testid="mojo-agent-backdrop"
      />
      <div
        id="mojo-agent-panel"
        ref={panelRef}
        role="complementary"
        aria-label="Mojo Agent"
        data-testid="mojo-agent-panel"
        data-layout-mode={layoutMode}
        data-panel-width={isPushLayout ? 'default' : panelWidth}
        className={[
          'flex flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700',
          'transition-[width,transform] duration-200 ease-out',
          isPushLayout
            ? 'fixed inset-y-0 right-0 z-[80] w-full max-w-md shadow-xl lg:shadow-none lg:static lg:z-auto lg:h-screen lg:w-[400px] lg:max-w-none lg:shrink-0'
            : overlayWidthClasses,
        ].join(' ')}
      >
        <MojoAgentPanel onClose={close} className="flex-1 min-h-0" />
      </div>
    </>
  );
}
